/**
 * Devices — the list view. DataGrid -> CardList reflow (sortable table on desktop,
 * tappable cards on tablet). Row click drills into the device detail Sheet/route.
 *
 * MR-2 additions:
 *   - Site column (name, or an "Unassigned" badge when site_id IS NULL) + Groups column.
 *   - Multi-select (checkbox column + card checkbox) with a bulk-action bar:
 *     assign the selection to a site, or add a group tag to the selection.
 *   - Filter bar: by Site (incl. Unassigned), Status, and Group/tag.
 * All filtering is client-side over the in-scope page (same pattern as the kind filter).
 *
 * Fleet-legibility additions:
 *   - Four status BUCKETS above the table (Offline / Degraded / Online / Never seen), each a
 *     filter. "Never seen" is the important one: registered-but-never-contacted used to be
 *     folded into Offline, so the offline count mixed real outages with paperwork.
 *     The COUNTS come from the server (useFleetStatusCounts), never from `rows` — see the
 *     comment at the <StatusBuckets> call site for the defect that forced that.
 *   - A "Config" column carrying the applied-state verdict, so "which devices are not running
 *     what we pushed?" is answerable by sorting a column instead of opening every device.
 *   - A 24h connectivity SPARKLINE per row, which answers "has this been stable?" — a
 *     different question from "is it up right now?", and the one that separates a flapping
 *     device from a steady one at a glance.
 *   - "Contacted at" as relative time, sorted by default, so the stragglers surface with no
 *     filter applied.
 *
 * Find-bar additions (operator report: "no search option, no filter per equipment type"):
 *   - A SEARCH field over serial / name / model / MAC / IP — the five identifiers someone
 *     actually has in hand when hunting a device (a label, a CAM-table MAC, a ping target).
 *   - An EQUIPMENT TYPE filter over the derived device kind.
 *   Both are sent to the SERVER, not applied to the fetched page — see the note above
 *   `useDevices` in lib/api/hooks for why a client-side search on a paged list cannot find
 *   the device you opened it for. Both are also forwarded to useFleetStatusCounts, so the
 *   tiles keep describing the rows underneath them while a search is active.
 *
 * The config and connectivity data are each fetched ONCE for the whole list and joined by
 * serial (Map lookups), never per row — these back columns on the busiest page in the product.
 */
import { firmwareShort } from "@/lib/firmwareShort";
import { formatPortRefs } from "@/lib/switchPorts";
import { lldpAge } from "@/lib/lldpAge";
import { descriptionPreview } from "@/lib/devicePhotos";
import { useEffect, useRef, useState } from "react";
import { Outlet, useNavigate, useSearchParams } from "react-router-dom";
import type { ColumnDef, Row } from "@tanstack/react-table";
import {
  useDevices,
  useAssignDevices,
  useBulkTag,
  useFleetConfigStatus,
  useFleetConnectivity,
  useFleetStatusCounts,
} from "@/lib/api/hooks";
import { useSession } from "@/stores/session";
import { orgLabel } from "@/lib/quarantine";
import { useEffectiveOrgFilter } from "@/hooks/useEffectiveOrgFilter";
import { DataGrid } from "@/components/data/DataGrid";
import { DeviceStatusPill } from "@/components/ui/StatusPill";
import { ConfigStatusPill, ConfigIncompatibleBadge } from "@/components/ui/ConfigStatusPill";
import { useSiteApBaseline } from "@/lib/api/apBaseline";
import { baselineBySerial, needsBaseline } from "@/lib/apBaselineView";
import { Sparkline } from "@/components/data/Sparkline";
import { ClientsCount } from "@/components/device/ClientsCount";
import { ApplyGroupSsids } from "@/components/device/ApplyGroupSsids";
import { StatusBuckets } from "@/components/data/StatusBuckets";
import { useScopeNames } from "@/components/data/ScopeLabel";
import { tileScopeLabel, deviceBucketScope, confinedSiteIds, reachOrgIds } from "@/lib/tileScope";
import { PageHeader, ErrorBanner } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { RoleGate } from "@/components/auth/RoleGate";
import type { Device } from "@/types/domain";
import { moveResultLines } from "@/lib/deviceMove";
import { CustomerDefaultOptIn } from "@/components/device/CustomerDefaultOptIn";
import { relTime, fmtAbsolute } from "@/lib/format";
import { DEVICE_KINDS, deviceKindLabel, devicesEmptyText, showApBaselineFilter } from "@/lib/deviceKinds";

/** Every bucket at zero. Module-level so the fallback is one stable object rather than a
 *  fresh one on every render. */
const ZERO_COUNTS = { online: 0, degraded: 0, offline: 0, provisioning: 0, dormant: 0 };

export default function Devices({
  kindFilter,
  title = "Devices",
  section = "devices",
}: {
  kindFilter?: string;
  title?: string;
  section?: string;
} = {}) {
  const navigate = useNavigate();

  // --- FIND state: the two controls the operator report said were missing ---------------
  // `searchInput` is what is typed; `search` is what is sent. They are separated by a
  // debounce so a fast typist costs one request, not one per keystroke (200ms, the same
  // figure the command palette uses — below the perception threshold, above key cadence).
  //
  // URL-BACKED (owner 2026-09-14: "a refresh clears the filters"). Every narrowing control
  // lives in the query string -- ?q=&type=&org=&site=&status=&group= -- so a browser refresh,
  // a shared link and Back all return the list the operator was reading. Writes use
  // `replace: true`: narrowing a list is not navigation and must not flood the history.
  const [params, setParams] = useSearchParams();
  const setParam = (key: string, value: string) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );

  // `search` (what is sent) is the URL's `q`; `searchInput` (what is typed) is local so the
  // box stays responsive, and is written to the URL after the debounce.
  const search = params.get("q") ?? "";
  const [searchInput, setSearchInput] = useState(search);
  // The last `q` THIS component wrote. Lets the sync effect below tell "the URL changed under
  // us" (a link, the command palette) from "our own debounced write landed" -- without it, a
  // write of "ab" landing while the operator has already typed "abc" would reset the box.
  const wroteQ = useRef(search);
  useEffect(() => {
    const t = setTimeout(() => {
      const value = searchInput.trim();
      if (value !== search) {
        wroteQ.current = value;
        setParam("q", value);
      }
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-arm on typing only
  }, [searchInput]);
  useEffect(() => {
    if (search !== wroteQ.current) {
      wroteQ.current = search;
      setSearchInput(search);
    }
  }, [search]);

  // Equipment type chosen in the control. IGNORED when this list is already a kind-filtered
  // section (/switches, /gateways, …): `kindFilter` comes from the route and outranks it, so
  // the two can never disagree about what the page is showing.
  const typeFilter = params.get("type") ?? "";
  const setTypeFilter = (v: string) => setParam("type", v);
  const effectiveKind = kindFilter || typeFilter || undefined;

  // Both go to the SERVER. Filtering the fetched page instead would search only the 100
  // most-recently-contacted devices — and a device worth hunting is usually one that stopped
  // checking in, i.e. exactly the rows that sort past the end of that page.
  const { data, isLoading, error } = useDevices({
    q: search || undefined,
    kind: effectiveKind,
  });
  // One request each for the whole page, joined by serial below. A per-row query here would
  // be an N+1 against the two endpoints that exist specifically to avoid one.
  const { data: configStatus } = useFleetConfigStatus();
  const { data: connectivity } = useFleetConnectivity(24);
  const orgId = useSession((s) => s.currentOrgId);
  const siteId = useSession((s) => s.currentSiteId);
  const me = useSession((s) => s.me);
  // ALL sites the user can access, across orgs (the backend /me already scopes this to the
  // caller). We intentionally do NOT filter to the current org: an msp_admin claims a device
  // INTO another customer's site (which moves the device to that customer), so the assign +
  // filter dropdowns must offer every reachable site. When the user spans multiple orgs, label
  // each site with its customer so a cross-org move is unambiguous.
  const allSites = me?.sites ?? [];
  const multiOrg = (me?.orgs?.length ?? 0) > 1;
  const orgName = (id: string) => me?.orgs.find((o) => o.id === id)?.name ?? "";
  const siteLabel = (s: { org_id: string; name: string }) =>
    multiOrg ? `${orgName(s.org_id)} / ${s.name}` : s.name;

  const assign = useAssignDevices();
  // OP#1935: bulk Assign of already-placed devices is a MOVE -- it changes only the placement
  // unless this (unchecked) opt-in is ticked; the result lines say what actually changed.
  const [applyDefault, setApplyDefault] = useState(false);
  const [assignResult, setAssignResult] = useState<{ lines: string[]; ok: boolean } | null>(null);
  const bulkTag = useBulkTag();
  // OP#1778 point 3: APs whose group-targeted SSIDs a bulk tag change moved (nothing pushed).
  const [tagSsidChanges, setTagSsidChanges] = useState<
    { serial: string; add: string[]; remove: string[] }[]
  >([]);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  // At SITE scope (the ScopeBar already names customer + site) the customer and site filters would only
  // duplicate it (owner 2026-09-14), so they are hidden and any stale ?site=/?org= in the URL is ignored.
  const scopedToSite = !!siteId;
  const siteFilter = scopedToSite ? "" : (params.get("site") ?? ""); // "" | "unassigned" | <siteId>
  const setSiteFilter = (v: string) => setParam("site", v);
  const statusFilter = params.get("status") ?? "";
  const setStatusFilter = (v: string) => setParam("status", v);
  const groupFilter = params.get("group") ?? "";
  const setGroupFilter = (v: string) => setParam("group", v);
  // "Needs AP baseline" (OP#1775). Only meaningful for ONE site: the status comes from a
  // read-only per-site roll-up (one owgw read per AP), so it is fetched only at site scope or
  // with a single site filtered, and the control is hidden otherwise.
  // AP lists only (review F12): on Switches / Gateways the control can only empty the table.
  const showBaseline = showApBaselineFilter(effectiveKind);
  // A stale ?baseline= on a non-AP list is ignored, not applied invisibly.
  const baselineFilter = showBaseline ? params.get("baseline") ?? "" : "";
  const setBaselineFilter = (v: string) => setParam("baseline", v);
  // Not fetched at all on a non-AP list (it costs one owgw read per AP at the site).
  const baselineSite = !showBaseline
    ? null
    : siteId
    ? String(siteId)
    : siteFilter && siteFilter !== "unassigned"
      ? siteFilter
      : null;
  const { data: apBaseline } = useSiteApBaseline(baselineSite);
  const baselineMap = baselineBySerial(baselineSite ? apBaseline : undefined);
  const [assignTo, setAssignTo] = useState("");
  const [tagInput, setTagInput] = useState("");
  // Customer filter (OP #1599). It used to come from `?org=` ALONE, so header Customer=GTK +
  // Site=All sites listed every customer's devices under an "All customers" dropdown. Now the
  // header customer is the default AND a lock: `orgLocked` pins the dropdown to it, and only a
  // header with no customer selected lets `?org=` (or "All customers") through. The rule lives
  // in lib/orgScope.ts so every list page resolves it identically. At site scope the dropdown
  // stays hidden as before; the pinned org is still the site's own org, so filtering by it is
  // a no-op there rather than a contradiction.
  const { orgFilter, locked: orgLocked } = useEffectiveOrgFilter(params.get("org"));  // "" | <orgId>
  const setOrgFilter = (v: string) => setParam("org", v);
  // Sites the Site FILTER may offer. Only sites of the pinned customer when the header pins
  // one: listing another customer's site there could only ever produce an empty list. The
  // assign dropdown below deliberately keeps `allSites` (cross-customer claim is legitimate).
  const filterSites = orgFilter ? allSites.filter((s) => String(s.org_id) === orgFilter) : allSites;
  const [lastIndex, setLastIndex] = useState<number | null>(null); // anchor for shift-click range

  // `all` is ALREADY narrowed by kind and by the search term — the server did both. The
  // browser-side `kindFilter` pass that used to live here is gone on purpose: it could only
  // ever filter the page it was given, so /switches on a 300-device customer listed the
  // switches that happened to fall in the 100 most-recently-contacted devices and silently
  // omitted the offline ones. Same defect the status tiles had; same fix.
  const all = data?.items ?? [];
  const allTags = Array.from(new Set(all.flatMap((d) => d.tags ?? []))).sort();

  // WHAT THE BUCKET TILES COUNT, and where the numbers come from.
  //
  // They count everything the other filters allow but NOT the status filter itself — if the
  // tiles counted the filtered rows, clicking "Offline" would leave "Offline 3" beside three
  // zeroes and the tiles would stop being a way to move between states.
  //
  // Those same filters are sent to the SERVER rather than applied to `rows` here. `rows` is
  // one page (100 devices) of a list ordered `last_seen DESC NULLS LAST`, and that ordering
  // sorts offline and never-seen devices past the end of it — so a page-derived count of
  // them was capable of reading 0 while forty APs were down. The tile that exists to say
  // "go look at these" could not show them.
  //
  // Session site vs. the Site dropdown: useDevices() already scopes to the session site, and
  // the dropdown narrows further on top of it, so the narrower of the two is what describes
  // the visible rows. When they contradict each other (a pinned site plus a different site
  // picked in the dropdown) the list is empty, and `bucketsConflict` keeps the tiles empty
  // with it rather than counting a site whose rows are not on screen.
  const bucketsConflict =
    !!siteId && !!siteFilter && siteFilter !== "unassigned" && siteFilter !== String(siteId);
  const { data: statusCounts } = useFleetStatusCounts({
    kind: effectiveKind,
    org_id: orgFilter || undefined,
    site_id: siteFilter || (siteId ? String(siteId) : undefined),
    tag: groupFilter || undefined,
    // The search term goes to the tiles too. A tile counting the whole fleet above a searched
    // list is the same lie in a new place: "2 Offline" over three search hits reads as "two of
    // these three are down".
    q: search || undefined,
  });
  // Undefined (still loading, or the request failed) renders as zeroes rather than falling
  // back to a page-derived count. A wrong number here is worse than no number: an operator
  // acts on "3 offline" and does not act on a blank tile, so the honest failure mode is the
  // one that prompts a refresh instead of a false all-clear.
  const bucketCounts = bucketsConflict ? ZERO_COUNTS : (statusCounts?.counts ?? ZERO_COUNTS);

  // OP#1787: say what the four tiles count. Built from the SAME values just sent to
  // useFleetStatusCounts (customer, site, type, group, search), so the label describes exactly
  // the set the server counted. The AP-baseline filter narrows only the table (it is applied to
  // the fetched rows, the counts endpoint has no such filter), so the label names it as NOT
  // applied rather than letting tiles and table silently disagree. The status filter is left
  // out on purpose (see "WHAT THE BUCKET TILES COUNT" above).
  const scopeNames = useScopeNames();
  const bucketSite = siteFilter || (siteId ? String(siteId) : "");
  const bucketScopeLabel = tileScopeLabel(
    deviceBucketScope(
      orgFilter,
      bucketSite,
      // A site-confined role's counts span its own sites only; an org-wide role's do not.
      confinedSiteIds(me, orgFilter || orgId),
      reachOrgIds(me),
    ),
    scopeNames,
    [
      { label: "type", value: effectiveKind ? deviceKindLabel(effectiveKind) : "" },
      { label: "site", value: siteFilter === "unassigned" ? "Unassigned" : "" },
      { label: "group", value: groupFilter },
      { label: "search", value: search },
    ],
    baselineFilter ? ["AP baseline"] : [],
  );

  const rows = all.filter((d) => {
    if (orgFilter && String(d.org_id) !== orgFilter) return false;
    if (siteFilter === "unassigned" && d.site_id != null) return false;
    if (siteFilter && siteFilter !== "unassigned" && String(d.site_id) !== siteFilter) return false;
    if (statusFilter && d.status !== statusFilter) return false;
    if (groupFilter && !(d.tags ?? []).includes(groupFilter)) return false;
    if (baselineFilter === "needs" && baselineSite && !needsBaseline(baselineMap, d.serial)) return false;
    return true;
  });

  // Carry the query string into the device Sheet's URL: DeviceDetail closes back to its
  // parent path WITH the search, so opening a device never costs the operator their filters.
  const listQuery = params.toString();
  const detailHref = (serial: string) =>
    (siteId ? `/o/${orgId}/s/${siteId}/${section}/${serial}` : `/o/${orgId}/${section}/${serial}`) +
    (listQuery ? `?${listQuery}` : "");

  // --- selection helpers ---
  const toggle = (serial: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(serial)) next.delete(serial);
      else next.add(serial);
      return next;
    });
  const visible = rows.map((d) => d.serial);
  const allVisibleSelected = visible.length > 0 && visible.every((s) => selected.has(s));
  const toggleAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visible.forEach((s) => next.delete(s));
      else visible.forEach((s) => next.add(s));
      return next;
    });
  const clearSel = () => setSelected(new Set());
  const selectedList = [...selected];

  // Shift-click range select: a plain click toggles one row and drops an anchor; Shift+click
  // ADDS every row between the anchor and this one, in the CURRENT visual (sorted) order — so
  // the range matches what the user sees regardless of the active sort. Mirrors file-manager
  // multi-select. `visualRows` is the table's live sorted row model.
  const selectClick = (visualIndex: number, visualRows: Row<Device>[], shift: boolean, serial: string) => {
    if (shift && lastIndex !== null && visualIndex >= 0) {
      const [a, b] = [Math.min(lastIndex, visualIndex), Math.max(lastIndex, visualIndex)];
      const range = visualRows.slice(a, b + 1).map((r) => r.original.serial);
      setSelected((prev) => {
        const next = new Set(prev);
        range.forEach((s) => next.add(s));
        return next;
      });
    } else {
      toggle(serial);
    }
    setLastIndex(visualIndex);
  };

  // Columns are rebuilt each render so the checkbox cells reflect current selection.
  const cbCls = "h-4 w-4 cursor-pointer accent-brand";
  const columns: ColumnDef<Device, unknown>[] = [
    {
      id: "select",
      enableSorting: false,
      header: () => (
        <input
          type="checkbox"
          className={cbCls}
          aria-label="Select all shown"
          checked={allVisibleSelected}
          onChange={toggleAll}
          onClick={(e) => e.stopPropagation()}
        />
      ),
      cell: (c) => (
        <input
          type="checkbox"
          className={cbCls}
          aria-label={`Select ${c.row.original.serial}`}
          checked={selected.has(c.row.original.serial)}
          // All selection logic runs in onClick (it carries e.shiftKey); onChange is a no-op so
          // the controlled input doesn't warn. Range uses the live sorted row model for correctness.
          onChange={() => {}}
          onClick={(e) => {
            e.stopPropagation();
            const vrows = c.table.getRowModel().rows;
            const vi = vrows.findIndex((r) => r.id === c.row.id);
            selectClick(vi, vrows, e.shiftKey, c.row.original.serial);
          }}
        />
      ),
    },
    {
      accessorKey: "name",
      header: "Name",
      // The installer note rides under the name: "behind the basement TV" is what finds a box.
      cell: (c) => {
        const note = descriptionPreview(c.row.original.description);
        return (
          <div className="min-w-0">
            <span className="font-medium">{c.getValue<string>()}</span>
            {note && (
              <div
                className="max-w-[22rem] truncate text-xs text-ink-muted"
                title={c.row.original.description ?? undefined}
              >
                {note}
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "serial",
      header: "Serial",
      cell: (c) => <span className="font-mono text-xs">{c.getValue<string>()}</span>,
    },
    { accessorKey: "model", header: "Model" },
    {
      // Short release label ("TIP 4.2.7", "Shasta", "EC 12.5.93"); the full owgw string is the
      // tooltip. Sorting uses the full string so builds of one release group together.
      accessorKey: "firmware",
      header: "Firmware",
      cell: (c) => {
        const fw = c.getValue<string | null | undefined>();
        return (
          <span className="font-mono text-xs whitespace-nowrap" title={fw ?? undefined}>
            {firmwareShort(fw)}
          </span>
        );
      },
    },
    {
      id: "site",
      header: "Site",
      cell: (c) => <SiteCell name={c.row.original.site_name} />,
    },
    {
      id: "groups",
      header: "Groups",
      enableSorting: false,
      cell: (c) => <TagChips tags={c.row.original.tags} />,
    },
    {
      accessorKey: "mac",
      header: "MAC",
      cell: (c) => <span className="font-mono text-xs">{c.getValue<string>() ?? "—"}</span>,
    },
    {
      // Where owgw saw the device connect FROM. Behind NAT this is the site's egress, so
      // every device at one site shares it -- it identifies the site, not the box.
      accessorKey: "ip",
      header: "Public IP",
      cell: (c) => <span className="font-mono text-xs">{c.getValue<string | null>() ?? "—"}</span>,
    },
    {
      // The device's own LAN / management address (gateway: its LAN; AP/switch: its upstream
      // DHCP address), from its state document or, failing that, its gateway's DHCP lease.
      accessorKey: "private_ip",
      header: "Private IP",
      cell: (c) => <span className="font-mono text-xs">{c.getValue<string | null>() ?? "—"}</span>,
    },
    {
      id: "uplink",
      header: "Uplink",
      cell: (c) => <span className="text-xs">{fmtLinkShort(c.row.original.uplink)}</span>,
    },
    {
      id: "lldp",
      header: "Connected to",
      cell: (c) => {
        const l = c.row.original.lldp;
        if (!l?.system_name) return "—";
        // The snapshot is last-KNOWN, never cleared (OP#1857): say how old it is. An offline
        // device reads "last seen 11d ago" and the whole cell is muted -- history, not now.
        const age = lldpAge(l, c.row.original.online, Date.now());
        return (
          <span className={age.stale ? "text-ink-muted" : undefined} data-testid="lldp-cell" data-stale={age.stale ? "true" : "false"}>
            <span className="font-mono text-xs">
              {l.system_name}
              {l.port_id ? ` · ${formatPortRefs(l.port_id)}` : ""}
            </span>
            <span className="block text-[11px] text-ink-subtle" data-testid="lldp-age">{age.label}</span>
          </span>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (c) => <DeviceStatusPill status={c.row.original.status} />,
    },
    {
      id: "config",
      header: "Config",
      // Sorted by SEVERITY, not alphabetically: the point of making this column sortable is
      // to bring the diverged devices to the top in one click, and "diverged" < "unverified"
      // < "up to date" is not a string order.
      accessorFn: (d) => configRank(configStatus?.get(d.serial)?.verdict),
      cell: (c) => {
        const st = configStatus?.get(c.row.original.serial);
        // The honest verdict, plus a neutral badge when the device is excluded from Meridian
        // config (services/config_compat) -- e.g. a stock-firmware AP that needs a reflash.
        return (
          <span className="inline-flex flex-wrap items-center gap-1">
            <ConfigStatusPill status={st} />
            <ConfigIncompatibleBadge
              excluded={st?.config_excluded}
              label={st?.config_excluded_label}
              reason={st?.config_excluded_reason}
            />
            <NeedsBaselineBadge show={needsBaseline(baselineMap, c.row.original.serial)} />
          </span>
        );
      },
    },
    {
      id: "connectivity",
      header: "24h",
      // Sorts by gap count, then by how much we actually observed: a device with three
      // outage hours outranks one with one, and an unobserved device sorts below both rather
      // than tying with a perfect record.
      accessorFn: (d) => {
        const t = connectivity?.get(d.serial);
        if (!t) return -1;
        return t.gap * 1000 + (t.observed === 0 ? -1 : 0);
      },
      cell: (c) => {
        const t = connectivity?.get(c.row.original.serial);
        return <Sparkline buckets={t?.buckets} summary={t} />;
      },
    },
    {
      // CONNECTED NOW (server: fresh sighting on an ONLINE device). An offline device shows
      // "—" plus a quiet "19 when last online · 1d ago" -- never a live-looking count. Sorts on
      // the connected-now number, so offline devices sort with the zeros.
      accessorKey: "clients_count",
      header: "Clients",
      cell: (c) => <ClientsCount device={c.row.original} />,
    },
    {
      accessorKey: "last_seen",
      header: "Contacted at",
      // Relative, because "3m ago" is the form an operator can act on without doing
      // arithmetic against the wall clock. The exact timestamp stays in the tooltip.
      cell: (c) => {
        const iso = c.getValue<string | null>();
        return (
          <span
            className={iso ? "text-ink" : "text-ink-subtle"}
            title={iso ? fmtAbsolute(iso) : "Never contacted the controller"}
          >
            {relTime(iso)}
          </span>
        );
      },
      // NULL (never contacted) must sort to the BOTTOM of a most-recent-first list, not the
      // top: a never-installed device is not the freshest thing in the fleet. -Infinity puts
      // it last under a descending sort without special-casing the comparator.
      sortingFn: (a, b) => {
        const t = (v: string | null) => (v ? new Date(v).getTime() : -Infinity);
        return t(a.original.last_seen) - t(b.original.last_seen);
      },
    },
  ];

  // Two select skins, one shape. The filter-bar selects sit directly on the page ground, so
  // the RAISED surface token separates them from it; the find-bar controls sit inside a card
  // that is already `bg-surface`, so they take the RECESSED well token instead. Same border,
  // same type scale — only the ground they stand on differs, which is what the two tokens are
  // for. Neither touches the status ramp: these are chrome, not device state.
  const selCls =
    "rounded border border-line bg-surface px-2 py-1 text-sm text-ink";
  // The single filter row sits on the page ground too; a touch taller so the selects line up
  // with the search box beside them.
  const filterCls =
    "rounded border border-line bg-surface px-2 py-1.5 text-sm text-ink";

  // Is anything narrowing the list right now? Drives the "Clear filters" escape hatch — a
  // page that can be filtered into an empty state needs a visible way back out of it, or the
  // empty state reads as "there are no devices".
  // A header-pinned customer (orgLocked) is the SCOPE, not a filter the operator applied: it
  // must not light up "Clear filters" (which could not clear it) or label the count "matching".
  const anyFilter =
    !!searchInput || !!typeFilter || !!siteFilter || !!statusFilter || !!groupFilter ||
    !!baselineFilter || (!!orgFilter && !orgLocked);
  const clearFilters = () => {
    wroteQ.current = "";
    setSearchInput("");
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const k of ["q", "type", "site", "status", "group", "org", "baseline"]) next.delete(k);
        return next;
      },
      { replace: true },
    );
  };

  // "N shown" is this PAGE; `statusCounts.total` is everything the current scope + search +
  // type match. Saying "of M" when they differ is what stops "12 shown" from being read as
  // "12 devices", and the word "matching" is what stops it being read as the whole fleet
  // while a search is active.
  const scopeTotal = statusCounts?.total;
  const subtitle =
    scopeTotal != null && scopeTotal > rows.length
      ? `${rows.length} shown of ${scopeTotal}${anyFilter ? " matching" : ""}`
      : `${rows.length} shown${anyFilter ? " matching" : ""}`;

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle} />

      {/* ONE FILTER ROW (owner 2026-09-14: search and type on the same line as the other
          filters). Search leads -- "which device is this?", a serial or MAC in hand, is still
          the commonest reason to open this page -- and takes the free width. On a narrow tablet
          it wraps to its own line first and the selects flow under it. Every control sits on
          the page ground, so all share the raised `bg-surface` skin. */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <SearchField
          value={searchInput}
          onChange={setSearchInput}
          // Naming the fields IN the placeholder is deliberate: an operator holding a MAC off a
          // switch CAM table has no way to know this box accepts it otherwise.
          placeholder="Search serial, name, model, MAC, IP or notes…"
        />
        <TypeFilterSelect
          value={typeFilter}
          onChange={setTypeFilter}
          lockedTo={kindFilter}
          className={filterCls}
        />
        {/* Customer (org) filter — only when the list spans multiple customers. */}
        {/* OP #1599: when the header pins a customer the control shows THAT customer and is
            disabled (one option, no "All customers") -- the header is where the customer is
            changed. Only a header with no customer offers the free choice below. */}
        {multiOrg && !scopedToSite && (
          orgLocked ? (
            <select
              className={filterCls}
              aria-label="Customer"
              value={orgFilter}
              disabled
              title="Change the customer in the scope bar"
            >
              <option value={orgFilter}>{orgName(orgFilter) || orgFilter}</option>
            </select>
          ) : (
            <select
              className={filterCls}
              aria-label="Customer"
              value={orgFilter}
              onChange={(e) => setOrgFilter(e.target.value)}
            >
              <option value="">All customers</option>
              {me?.orgs.map((o) => (
                // Quarantine holding area labelled, not "Default Organization" (2026-10-19).
                <option key={o.id} value={o.id}>{orgLabel(o)}</option>
              ))}
            </select>
          )
        )}
        {!scopedToSite && (
          <select
            className={filterCls}
            aria-label="Site"
            value={siteFilter}
            onChange={(e) => setSiteFilter(e.target.value)}
          >
            <option value="">All sites</option>
            <option value="unassigned">Unassigned</option>
            {/* Narrowed to the effective customer (OP #1599) -- see filterSites. */}
            {filterSites.map((s) => (
              <option key={s.id} value={s.id}>{siteLabel(s)}</option>
            ))}
          </select>
        )}
        <select
          className={filterCls}
          aria-label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">Any status</option>
          <option value="online">Online</option>
          <option value="degraded">Degraded</option>
          <option value="offline">Offline</option>
          <option value="provisioning">Provisioning</option>
          <option value="dormant">Never seen</option>
        </select>
        {allTags.length > 0 && (
          <select
            className={filterCls}
            aria-label="Group"
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
          >
            <option value="">All groups</option>
            {allTags.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        )}
        {baselineSite && (
          <select
            className={filterCls}
            aria-label="AP baseline"
            value={baselineFilter}
            onChange={(e) => setBaselineFilter(e.target.value)}
          >
            <option value="">Any AP baseline</option>
            <option value="needs">
              Needs AP baseline{apBaseline ? ` (${apBaseline.needs_baseline})` : ""}
            </option>
          </select>
        )}
        {anyFilter && (
          <Button variant="ghost" onClick={clearFilters}>
            Clear filters
          </Button>
        )}
      </div>

      {/* Status buckets — counted SERVER-SIDE over everything in scope, before the status
          filter is applied, so the numbers stay stable while you click between them and do
          not silently describe only the first page. See bucketCounts above. */}
      <StatusBuckets
        counts={bucketCounts}
        active={statusFilter}
        onSelect={setStatusFilter}
        scope={bucketScopeLabel}
      />

      {/* Bulk-action bar (only when a selection exists; write-gated) */}
      {selectedList.length > 0 && (
        <RoleGate min="site_operator" mode="hide">
          <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-line bg-surface-2 p-3">
            <span className="text-sm font-medium text-ink">{selectedList.length} selected</span>

            <div className="flex items-center gap-1.5">
              <select className={selCls} value={assignTo} onChange={(e) => setAssignTo(e.target.value)}>
                <option value="">Assign to site…</option>
                {allSites.map((s) => (
                  <option key={s.id} value={s.id}>{siteLabel(s)}</option>
                ))}
              </select>
              {/* POST /devices/assign is @require("org_admin") server-side: below that the
                  button is disabled with the reason (UI review 2026-10-17). Tags stay open. */}
              <RoleGate min="org_admin" mode="disable" reason="Adding or moving devices between sites requires a customer admin.">
                <Button
                  variant="primary"
                  disabled={!assignTo || assign.isPending}
                  onClick={() => {
                    const toName = allSites.find((s) => String(s.id) === assignTo)?.name ?? "";
                    assign.mutate(
                      {
                        serials: selectedList,
                        site_id: Number(assignTo),
                        ...(applyDefault ? { apply_customer_default: true } : {}),
                      },
                      {
                        onSuccess: (r) => {
                          setAssignResult({ lines: moveResultLines(r, toName), ok: r.assigned > 0 });
                          clearSel();
                          setAssignTo("");
                          setApplyDefault(false);
                        },
                      },
                    );
                  }}
                >
                  Assign
                </Button>
              </RoleGate>
            </div>

            <div className="flex items-center gap-1.5">
              <input
                className={selCls}
                placeholder="group tag"
                list="ap-group-tags"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
              />
              {/* Suggest the tags that already exist so Remove tag is a pick, not a retype. */}
              <datalist id="ap-group-tags">
                {allTags.map((t) => (
                  <option key={t} value={t} />
                ))}
              </datalist>
              <Button
                variant="secondary"
                disabled={!tagInput.trim() || bulkTag.isPending}
                onClick={() =>
                  bulkTag.mutate(
                    { serials: selectedList, add: [tagInput.trim()] },
                    {
                      onSuccess: (res) => {
                        clearSel();
                        setTagInput("");
                        setTagSsidChanges(res.ssid_changes ?? []);
                      },
                    },
                  )
                }
              >
                Add tag
              </Button>
              {/* Remove the typed tag from every selected AP. The bulk endpoint already
                  supports `remove`; only this control was missing (an AP could be tagged
                  but never un-tagged from the list). Removing a tag never touches the
                  device config -- it only changes group membership. */}
              <Button
                variant="ghost"
                disabled={!tagInput.trim() || bulkTag.isPending}
                onClick={() =>
                  bulkTag.mutate(
                    { serials: selectedList, remove: [tagInput.trim()] },
                    {
                      onSuccess: (res) => {
                        clearSel();
                        setTagInput("");
                        setTagSsidChanges(res.ssid_changes ?? []);
                      },
                    },
                  )
                }
              >
                Remove tag
              </Button>
            </div>

            <Button variant="ghost" onClick={clearSel}>Clear</Button>
            {assignTo && (
              <div className="basis-full">
                <CustomerDefaultOptIn
                  orgId={allSites.find((s) => String(s.id) === assignTo)?.org_id}
                  checked={applyDefault}
                  onChange={setApplyDefault}
                />
              </div>
            )}
          </div>
        </RoleGate>
      )}
      {assignResult && (
        <ul
          className={`mb-3 text-xs ${assignResult.ok ? "text-healthy" : "text-warn"}`}
          data-testid="bulk-move-result"
        >
          {assignResult.lines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}
      {tagSsidChanges.length > 0 && (
        <RoleGate min="site_operator" mode="hide">
          <div className="mb-3 flex flex-col gap-2">
            {tagSsidChanges.map((c) => (
              <div key={c.serial}>
                <p className="text-xs font-medium text-ink">{c.serial}</p>
                <ApplyGroupSsids
                  serial={c.serial}
                  changes={c}
                  onDone={() => setTagSsidChanges((prev) => prev.filter((x) => x.serial !== c.serial))}
                />
              </div>
            ))}
          </div>
        </RoleGate>
      )}

      {error ? (
        <ErrorBanner error={error} />
      ) : (
        <DataGrid<Device>
          data={rows}
          columns={columns}
          isLoading={isLoading}
          rowKey={(d) => d.serial}
          // Opens on most-recently-contacted first. The default order is an editorial choice:
          // whatever sits at the top is what gets looked at, and recency puts the devices that
          // have stopped checking in where someone will see them without applying a filter.
          initialSorting={[{ id: "last_seen", desc: true }]}
          onRowActivate={(d) => navigate(detailHref(d.serial))}
          empty={devicesEmptyText({ search, anyFilter, kind: effectiveKind, atSite: !!siteId })}
          renderCard={(d) => (
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                className={`${cbCls} mt-1`}
                aria-label={`Select ${d.serial}`}
                checked={selected.has(d.serial)}
                onChange={() => toggle(d.serial)}
                onClick={(e) => e.stopPropagation()}
              />
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-ink">{d.name}</div>
                {descriptionPreview(d.description) && (
                  <div className="mt-0.5 truncate text-xs text-ink-muted">
                    {descriptionPreview(d.description)}
                  </div>
                )}
                {/* Card (tablet) view: one lean address token. The private IP is the one you
                    can reach the box on, so it wins; the public IP only when it is all we have. */}
                <div
                  className="mt-0.5 truncate font-mono text-xs text-ink-subtle"
                  title={
                    [d.private_ip && `Private IP ${d.private_ip}`, d.ip && `Public IP ${d.ip}`]
                      .filter(Boolean)
                      .join(" · ") || undefined
                  }
                >
                  {d.serial}
                  {d.private_ip ? ` · ${d.private_ip}` : d.ip ? ` · ${d.ip} (public)` : ""}
                </div>
                <div className="mt-0.5 truncate text-xs text-ink-muted">
                  {d.model} · {firmwareShort(d.firmware)} · {d.site_name ?? "Unassigned"}
                  {(d.tags?.length ?? 0) > 0 ? ` · ${d.tags!.join(", ")}` : ""}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <DeviceStatusPill status={d.status} />
                {/* The card reflow keeps the same three facts as the table row — state,
                    config, stability — so a tablet operator is not working from less. */}
                <ConfigStatusPill status={configStatus?.get(d.serial)} />
                <ConfigIncompatibleBadge
                  excluded={configStatus?.get(d.serial)?.config_excluded}
                  label={configStatus?.get(d.serial)?.config_excluded_label}
                  reason={configStatus?.get(d.serial)?.config_excluded_reason}
                />
                <NeedsBaselineBadge show={needsBaseline(baselineMap, d.serial)} />
                <Sparkline
                  buckets={connectivity?.get(d.serial)?.buckets}
                  summary={connectivity?.get(d.serial)}
                  className="h-3 w-24"
                />
                <span className="text-xs text-ink-subtle">{relTime(d.last_seen)}</span>
              </div>
            </div>
          )}
        />
      )}

      {/* PAGE TRUNCATION, said out loud. The list is one page; when the server holds more
          matches than it sent, an operator who searched and got 100 hits must not read those
          100 as "all of them" and conclude the device is not there. `data.total` is the
          server's count for exactly this query, so the comparison is exact. */}
      {data && data.items.length < data.total && (
        <p className="mt-2 text-xs text-ink-subtle">
          Showing the first {data.items.length} of {data.total} matches. Narrow the search or
          the filters to see the rest.
        </p>
      )}

      {/* Nested detail route renders its Sheet OVER this list (list stays mounted). */}
      <Outlet />
    </div>
  );
}

/**
 * SearchField — the device search box.
 *
 * A plain input would have satisfied the ticket; this carries three extras that are the
 * difference between a control that exists and one that gets used:
 *   - a magnifier glyph, so the field is recognisable as search before the placeholder is read;
 *   - a clear button, because the fastest way to recover from a search that found nothing is
 *     to undo it in one tap (the native ::-webkit-search-cancel-button is suppressed so the
 *     affordance looks and sits the same in every browser, and is a 44px touch target);
 *   - `type="search"`, so assistive tech announces it as one.
 * The focus ring is the BRAND ring from the global :focus-visible rule — the status ramp means
 * device state and must never be spent on chrome.
 */
function SearchField({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div className="relative min-w-[14rem] flex-1 basis-64">
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <circle cx="11" cy="11" r="7" />
        <path d="M20 20l-3.5-3.5" />
      </svg>
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Search devices by serial, name, model, MAC, IP or notes"
        placeholder={placeholder}
        className={
          "w-full rounded-md border border-line bg-surface py-1.5 pl-9 pr-10 text-sm text-ink " +
          "placeholder:text-ink-subtle [&::-webkit-search-cancel-button]:appearance-none"
        }
      />
      {value && (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => onChange("")}
          className="absolute right-0 top-1/2 flex h-11 w-10 -translate-y-1/2 items-center justify-center text-ink-subtle hover:text-ink"
        >
          <span aria-hidden="true" className="text-lg leading-none">
            ×
          </span>
        </button>
      )}
    </div>
  );
}

/**
 * TypeFilterSelect — filter by equipment type (the derived device kind).
 *
 * WHY IT STILL RENDERS ON /switches, /gateways AND FRIENDS, DISABLED, RATHER THAN HIDING.
 * Those routes mount this same list with a `kindFilter` from the route, so the type is
 * already decided and the control must not be able to contradict it. The choice was hide it
 * or show it locked; locked wins on two counts:
 *   1. Hiding it recreates the exact complaint on five of the six pages that show devices —
 *      an operator on /switches would again find a device list with no visible type control
 *      and no way to tell whether one exists.
 *   2. It EXPLAINS the page. A locked "Switch" is the page saying why it is not listing the
 *      AP someone just searched for; without it, a narrowed list looks like a broken one.
 * The all-devices list (/devices) is where it is live, which is also where it is the answer
 * to "filter per equipment type".
 */
function TypeFilterSelect({
  value,
  onChange,
  lockedTo,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  /** Kind fixed by the route (e.g. "switch" on /switches); undefined on the all-devices list. */
  lockedTo?: string;
  className: string;
}) {
  if (lockedTo) {
    return (
      <select
        className={`${className} cursor-not-allowed text-ink-muted`}
        value={lockedTo}
        disabled
        aria-label="Equipment type (fixed by this section)"
        title={`This section only lists ${deviceKindLabel(lockedTo).toLowerCase()}s. Open Devices to filter the whole fleet by type.`}
        onChange={() => {}}
      >
        <option value={lockedTo}>{deviceKindLabel(lockedTo)}</option>
      </select>
    );
  }
  return (
    <select
      className={className}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label="Filter by equipment type"
    >
      {/* "All equipment types" rather than "All types": the word the operator used is
          "equipment type", and the label has to survive being read next to "All sites". */}
      <option value="">All equipment types</option>
      {DEVICE_KINDS.map((k) => (
        <option key={k.kind} value={k.kind}>
          {k.label}
        </option>
      ))}
    </select>
  );
}

/**
 * Config-verdict sort rank, worst first.
 *
 * Deliberately puts DIVERGED above UNVERIFIED: a device we know is wrong needs attention
 * before one we simply have not checked. And "verified" ranks last rather than being treated
 * as the absence of a problem — sorting this column is how an operator finds work, so the
 * devices with no work sink.
 */
function configRank(verdict?: string): number {
  if (verdict === "diverged") return 0;
  if (verdict === "verified") return 2;
  return 1; // unknown / missing row — never ranked as good news
}

/** Site cell: the site name, or an "Unassigned" badge when the device has no site. */
function SiteCell({ name }: { name?: string | null }) {
  if (name) return <span>{name}</span>;
  return (
    <span className="rounded bg-surface-2 px-1.5 py-0.5 text-xs text-ink-muted">Unassigned</span>
  );
}

/** Group tag chips (a group is a tag). */
function TagChips({ tags }: { tags?: string[] }) {
  if (!tags || tags.length === 0) return <span className="text-ink-subtle">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {tags.map((t) => (
        <span key={t} className="rounded bg-brand/10 px-1.5 py-0.5 text-xs text-brand">
          {t}
        </span>
      ))}
    </div>
  );
}

/** Compact uplink speed for the table/card: "2.5G", "1G", or "link down". */
function fmtLinkShort(u?: Device["uplink"]): string {
  if (!u || u.speed_mbps == null) return "link down";
  return u.speed_mbps >= 1000 ? `${u.speed_mbps / 1000}G` : `${u.speed_mbps}M`;
}

/** Neutral chip: this AP still has the factory routed LAN / 6 GHz on an open SSID. Apply it
 *  per AP from the device sheet's "AP baseline" card (never implicitly). */
function NeedsBaselineBadge({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <span
      className="inline-flex items-center rounded border border-warn/40 bg-warn-soft px-1.5 py-0.5 text-[0.6875rem] font-semibold text-ink"
      title="Open the AP and use AP baseline -> Preview / apply (one AP at a time, ~4 min outage each)"
    >
      Needs AP baseline
    </span>
  );
}
